import { Card } from '@cortex/web-design-system/components/ui/Card';
import type { PurchaseChartPoint } from '../../lib/purchase-aggregation.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 購入分析の KPI サマリカード。当期購入率・前期比、購入単価・前期比、購入着数、購入売上を表示。
 * @graph-connects none
 */
export function PurchaseKpiCards({ data }: { data: PurchaseChartPoint[] }) {
  const current = data[data.length - 1];
  const prev = data[data.length - 2];

  /** @graph-connects none */
  function delta(cur: number | undefined, pre: number | undefined): string {
    if (cur === undefined || pre === undefined || pre === 0) return '—';
    const diff = ((cur - pre) / pre) * 100;
    const sign = diff > 0 ? '+' : '';
    return `${sign}${diff.toFixed(1)}%`;
  }

  /** @graph-connects none */
  function deltaColor(cur: number | undefined, pre: number | undefined): string {
    if (cur === undefined || pre === undefined) return 'var(--color-text-muted)';
    return cur >= pre ? 'var(--color-success, #16a34a)' : 'var(--color-danger, #dc2626)';
  }

  /** @graph-connects none */
  const cards = [
    {
      label: '購入率',
      value: current ? `${(current.purchaseRate * 100).toFixed(1)}%` : '—',
      sub: '購入着数/発送着数',
      delta: delta(current?.purchaseRate, prev?.purchaseRate),
      color: deltaColor(current?.purchaseRate, prev?.purchaseRate),
    },
    {
      label: '平均購入単価',
      value: current ? `¥${current.averageUnitPrice.toLocaleString()}` : '—',
      sub: '税込',
      delta: delta(current?.averageUnitPrice, prev?.averageUnitPrice),
      color: deltaColor(current?.averageUnitPrice, prev?.averageUnitPrice),
    },
    {
      label: '購入着数',
      value: current ? `${current.purchaseCount.toLocaleString()}着` : '—',
      sub: '期間合計',
      delta: delta(current?.purchaseCount, prev?.purchaseCount),
      color: deltaColor(current?.purchaseCount, prev?.purchaseCount),
    },
    {
      label: '購入売上',
      value: current ? `¥${current.purchaseRevenue.toLocaleString()}` : '—',
      sub: '税込合計',
      delta: delta(current?.purchaseRevenue, prev?.purchaseRevenue),
      color: deltaColor(current?.purchaseRevenue, prev?.purchaseRevenue),
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {cards.map((card) => (
        <Card key={card.label} className="p-4">
          <p className="text-sm text-[var(--color-text-secondary)]">{card.label}</p>
          <p className="mt-1 text-2xl font-bold text-[var(--color-text-primary)]">{card.value}</p>
          <div className="mt-1 flex items-center gap-2">
            <span className="text-xs text-[var(--color-text-muted)]">{card.sub}</span>
            <span className="text-xs font-medium" style={{ color: card.color }}>
              {card.delta}
            </span>
          </div>
        </Card>
      ))}
    </div>
  );
}
